import {
  Button,
  LabeledInput,
  useAlert,
  useToast,
} from "@apex/component-library";
import { Text, Wrap, WrapItem } from "@chakra-ui/react";
import { AxiosError } from "axios";
import { useFormikContext } from "formik";
import { useEffect, useState } from "react";

import { useGetAccountDetails } from "../../api/useGetAccountDetails";
import useGetCallsAndAlertsOpenDetails from "../../api/useGetCallsAndAlertsOpenDetails";
import { useGetCorrespondentSummary } from "../../api/useGetCorrespondentSummary";
import displayError from "../../utils/displayError";
import InputFieldWrapper from "../InputFieldWrapper";

export interface AccountLookupFormValues {
  accountId: string;
}

interface AccountLookupProps {
  lookupAccountId: string;
}

const AccountLookup = ({ lookupAccountId }: AccountLookupProps) => {
  const {
    handleChange,
    values,
    touched,
    resetForm,
    handleBlur,
    errors: formikErrors,
    handleSubmit: handleFormikSubmit,
  } = useFormikContext<AccountLookupFormValues>();

  const { setAlert } = useAlert();
  const toast = useToast();
  const { refetch: getAccountDetails, isFetching: isFetchingAccount } =
    useGetAccountDetails(values.accountId, {
      onError: (error: AxiosError) => {
        const status = error.response?.status || error.request.status;
        // Do not display an error toast if the account is not found
        if (status !== 404) {
          displayError(setAlert, toast, error);
        }
        if (error.response?.status === 404) {
          setErrorMessage("Account not found. Try again.");
        }
        if (error.response?.status === 400) {
          setMarginErrorMessage(
            "Check the account number. The system does not currently support multiple margin accounts."
          );
        }
      },
    });

  const {
    refetch: getCorrespondentSummary,
    isFetching: isFetchingCorrespondent,
  } = useGetCorrespondentSummary(values.accountId);

  const { refetch: getCallsAndAlerts } = useGetCallsAndAlertsOpenDetails(
    values.accountId
  );

  const [errorMessage, setErrorMessage] = useState("");
  const [marginErrorMessage, setMarginErrorMessage] = useState("");

  useEffect(() => {
    if (lookupAccountId === "") {
      resetForm();
    }
  }, [lookupAccountId, resetForm]);

  const handleSubmit = () => {
    setMarginErrorMessage("");
    setErrorMessage("");
    getAccountDetails();
    getCorrespondentSummary();
    handleFormikSubmit();
    getCallsAndAlerts();
  };

  return (
    <>
      <form
        onSubmit={e => {
          e.preventDefault();
          handleSubmit();
        }}
      >
        <Wrap>
          <WrapItem>
            <InputFieldWrapper>
              <LabeledInput
                width="input.md"
                label="Account ID"
                name="accountId"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.accountId}
                errorMessage={formikErrors.accountId || errorMessage}
                isInvalid={
                  !!errorMessage ||
                  (touched.accountId && !!formikErrors.accountId)
                }
                isRequired
                data-testid="account-id-lookup-input"
              />
            </InputFieldWrapper>
          </WrapItem>
          <WrapItem alignItems="center" paddingTop="1rem">
            <Button
              isDisabled={!!formikErrors.accountId || !values.accountId}
              isLoading={isFetchingAccount || isFetchingCorrespondent}
              variant="primary"
              type="submit"
              formNoValidate
              data-testid="account-id-lookup-button"
            >
              Look-up
            </Button>
          </WrapItem>
        </Wrap>
      </form>
      {marginErrorMessage && <Text>{marginErrorMessage}</Text>}
    </>
  );
};

export default AccountLookup;
